import { useState } from "react";
import CustomButton from "../../components/CustomButton";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import classes from "../../components/Modal.module.css";
import { Loader } from "lucide-react";
import ErrorMessage from "../../components/ErrorMessage";
import saveIcon from "../../assets/save_icon.png";
import CustomInput from "../../components/CustomInput";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import { createEducationLevel } from "../../services/educationLevelServices";
import AddAnotherButton from "../../components/AddAnotherButton";
import SectionDivider from "../../components/SectionDivider";
import ErrorBox from "../../components/ErrorBox";

export default function AddEduStageModal({ setShowAddStageModal, onSuccess }) {
  const { t } = useTranslation("general");
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    educationStageName: "",
    educationStagePrice: "",
  });
  const [subclasses, setSubclasses] = useState([
    { name: "", createdAt: new Date() },
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) setError("");
  };

  const handleSubclassNameChange = (id, value) => {
    setSubclasses((prevSubclasses) =>
      prevSubclasses.map((subclass) =>
        subclass.id === id ? { ...subclass, name: value } : subclass
      )
    );
  };

  const validateForm = () => {
    if (!formData.educationStageName.trim()) {
      setError("educationStageNameRequired");
      return false;
    }
    if (!formData.educationStagePrice || formData.educationStagePrice < 0) {
      setError("validPriceRequired");
      return false;
    }

    if (subclasses.length === 0 || subclasses.every((c) => c.name === "")) {
      setError("shouldAddAtLeastOneClass");
      return false;
    }
    return true;
  };

  const handleAddEducationLevel = async () => {
    if (!validateForm()) return;

    setLoadingModal(true);
    setError("");

    try {
      const educationLevelData = {
        name: formData.educationStageName.trim(),
        price: parseFloat(formData.educationStagePrice) || 0,
        subClasses: subclasses
          .map((subclass) => ({
            name: subclass.name.trim(),
            createdAt: subclass.createdAt,
          }))
          .filter((subclass) => subclass.name !== ""),
      };

      await createEducationLevel(educationLevelData);

      // Call onSuccess callback if provided (for refreshing parent data)
      if (onSuccess) {
        onSuccess();
      }

      setShowAddStageModal(false);
    } catch (err) {
      console.error("Error creating education level:", err);

      // Handle specific error messages from backend
      if (err.response?.data?.error) {
        const backendError = err.response.data.error;
        if (backendError === "Education level name already exists") {
          setError("educationLevelAlreadyExists");
        } else if (backendError === "Name is required") {
          setError("educationStageNameRequired");
        } else {
          setError("failedToCreateEducationLevel");
        }
      } else {
        setError("failedToCreateEducationLevel");
      }
    } finally {
      setLoadingModal(false);
    }
  };

  const handleCancel = () => {
    setShowAddStageModal(false);
  };

  const handleAddSubclass = () => {
    setSubclasses((prevSubclasses) => [
      ...prevSubclasses,
      { id: prevSubclasses.length, name: "", createdAt: new Date() },
    ]);
  };

  return (
    <>
      <Modal
        title="addAnEduStage"
        onClose={handleCancel}
        error={t(error)}
        footer={
          <>
            {loadingModal ? <Loader className="animate-spin" /> : null}
            <section className={classes.actions}>
              <CustomButton
                onClick={handleAddEducationLevel}
                title={t("confirm")}
                icon={saveIcon}
                iconSize={16}
                disabled={loadingModal}
              />
              <CustomButton
                title={t("cancel")}
                onClick={handleCancel}
                variant="blank"
              />
            </section>
          </>
        }
      >
        {error && (
          <div className="o-row o-row--horizontal-align_center o-row--vertical-align_center">
            <ErrorBox>{t(error)}</ErrorBox>
          </div>
        )}
        <div className="o-column o-column--gap-size_400 u-padding--top_400 u-padding--bottom_400">
          <div className="o-row o-row--gap-size_700 o-row--vertical-align_center u-min-width-specific_400">
            <span className="u-min-width-specific_80">
              {t("educationStageName")}:
            </span>
            <CustomInput
              name="educationStageName"
              value={formData.educationStageName}
              onChange={handleInputChange}
              errorText={
                error === "educationStageNameRequired" ||
                error === "educationLevelAlreadyExists"
                  ? t(error)
                  : null
              }
              flex={true}
            />
          </div>
          <div className="o-row o-row--gap-size_700 o-row--vertical-align_center u-min-width-specific_400">
            <span className="u-min-width-specific_80">{t("price")}:</span>
            <CustomInputWithUnit
              name="educationStagePrice"
              value={formData.educationStagePrice}
              onChange={handleInputChange}
              errorText={error === "validPriceRequired" ? t(error) : null}
              flex={true}
              unit={t("IQD")}
              type="moneyV2"
              min={0}
            />
          </div>

          <div>
            <SectionDivider title="subclasses" />
            {error === "shouldAddAtLeastOneClass" && (
              <div className="error-message">
                {t("shouldAddAtLeastOneClass")}
              </div>
            )}
            <div className="o-column o-column--gap-size_300">
              {subclasses.map((subclass) => (
                <div
                  key={subclass.id}
                  className="o-row o-row--gap-size_700 o-row--vertical-align_center"
                >
                  <span className="u-min-width-specific_80">
                    {t("subclassName")}:
                  </span>
                  <CustomInput
                    type="text"
                    value={subclass.name}
                    onChange={(e) =>
                      handleSubclassNameChange(subclass.id, e.target.value)
                    }
                    flex={true}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="o-row o-row--horizontal-align_end u-width_full-width u-padding--bottom_200 u-padding--top_200">
            <AddAnotherButton
              onClick={handleAddSubclass}
              title={t("addSubclass")}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
